import {
    useCallback, useMemo,
} from 'react'
import AddIcon from '@mui/icons-material/Add'

import poolingDashboardRequests from 'app/requests/Order/PoolingDashboard.requests'
import ordersRequest, {
    DailyOpsQueryNames,
} from 'app/requests/Order/Order.requests'
import SkyNetTable from 'app/shared-components/SkyNetTable'
import Button from 'app/shared-components/Button'
import useHasPermission from 'app/hooks/useHasPermission'
import {
    RoutesConfig,
} from 'app/SkyNetRoutes/SkyNetRoutes.types'
import {
    TableControlTypes,
} from 'app/shared-components/TableControls'

import useOrderManagementRoutes from 'app/SkyNetRoutes/hooks/useOrderManagementRoutes'
import {
    OrderTypes,
} from 'app/enums/label.enums'
import {
    Order,
} from '../Orders/orders.types'
import useStyles from './DailyOps.style'
import toBeConfirmedConfig from './configs/toBeConfirmed.config'
import deliveryViaRoadPickUpConfig from './configs/deliveryViaRoadPickUp.config'
import deliveryViaRoadInfoConfig from './configs/deliveryViaRoadInfo.config'
import deliveryViaRoadConfirmConfig from './configs/deliveryViaRoadConfirm.config'
import pickupViaRoadConfig from './configs/pickupViaRoad.config'
import airTransportConfig from './configs/airTransport.config'
import getConfirmationDropOffConfig from './configs/confirmation.config'
import missingInformationConfig from './configs/missingInformation.config'
import containerSelectionConfig from './configs/containerSelection.config'
import pqOrdersConfig from './configs/pqOrdersConfig'
import changeRequestApprovalConfig from './configs/changeRequestApproval.config'
import {
    getDailyOpsNavigate,
} from './DailyOps.utils'

const DOMAIN_NAME = 'DailyOpsView'
const UNIQ_FIELD = 'orderNumber'

const tableControls: TableControlTypes[] = [
    TableControlTypes.ClearFilters,
    TableControlTypes.ClearSorting,
]

const DailyOps = () => {
    const {
        classes,
    } = useStyles()
    const Routes = useOrderManagementRoutes()

    const hasAutomaticQuotePermission: boolean = useHasPermission(['contract_basis_automatic_quote'])

    const openCustomerCreateForm = useCallback((): void => {
        Routes.CustomerOrders.CreateDirectLease.navigate()
    }, [Routes])

    const openCustomerCreateFormNew = useCallback((): void => {
        Routes.CustomerOrders.CreateAirlineLease.navigate()
    }, [Routes])

    const openInternalCreateForm = useCallback((): void => {
        Routes.InternalOrders.Create.navigate()
    }, [Routes])

    const onRowClick = useCallback((name: string, {
        orderNumber,
        orderType,
    }: Order): void => {
        return getDailyOpsNavigate({
            name,
            Route: (orderType === OrderTypes.CUSTOMER_TRANSPORT
                ? Routes.CustomerOrders
                : Routes.InternalOrders) as RoutesConfig,
        })({
            params: {
                key: orderNumber,
            },
        })
    }, [Routes])

    const urls: {
        pqOrders: string;
    } & {
        [K in DailyOpsQueryNames]: string;
    } = useMemo(() => {
        return {
            pqOrders: poolingDashboardRequests.dailyOpsPqOrders().url,
            ...Object.values(DailyOpsQueryNames)
                .reduce<Record<DailyOpsQueryNames, string>>((acc, queryName) => {
                    return {
                        ...acc,
                        [queryName]: ordersRequest.dailyOps({
                            queryName,
                        }).url,
                    }
                }, {} as Record<DailyOpsQueryNames, string>),
        }
    }, [])

    return (
        <div className={classes.root}>
            <div className={classes.headerTitle}>
                Customer Transports
                <div className={classes.actionWrapper}>
                    <Button
                        label="Create Direct Lease"
                        onClick={openCustomerCreateForm}
                        data-testid="openCustomerCreateFormBtn"
                        icon={<AddIcon />}
                        className={classes.button}
                    />
                    {
                        hasAutomaticQuotePermission && (
                            <Button
                                label="Create AirLine Lease"
                                onClick={openCustomerCreateFormNew}
                                data-testid="openCustomerCreateFormBtnNew"
                                icon={<AddIcon />}
                                className={classes.button}
                            />
                        )
                    }
                </div>
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name={DOMAIN_NAME}
                    onRowClick={(args) => {
                        onRowClick('pqOrders', args)
                    }}
                    customUrl={urls.pqOrders}
                    tableConfig={pqOrdersConfig}
                    uniqField={UNIQ_FIELD}
                    isShowCounterTab
                    showSearch
                    data-testid="pqOrders"
                    paginationModeMatches
                    tableControls={tableControls}
                    className={classes.table}
                />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name={DOMAIN_NAME}
                    onRowClick={(args) => {
                        onRowClick('changeRequestApproval', args)
                    }}
                    customUrl={urls.changePendingApproval}
                    tableConfig={changeRequestApprovalConfig}
                    uniqField={UNIQ_FIELD}
                    isShowCounterTab
                    paginationModeMatches
                    showSearch
                    data-testid="changeRequestApproval"
                    tableControls={tableControls}
                    className={classes.table}
                />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name={DOMAIN_NAME}
                    onRowClick={(args) => {
                        onRowClick('toBeConfirmed', args)
                    }}
                    customUrl={urls.toBeConfirmed}
                    tableConfig={toBeConfirmedConfig}
                    uniqField={UNIQ_FIELD}
                    isShowCounterTab
                    paginationModeMatches
                    showSearch
                    data-testid="toBeConfirmed"
                    tableControls={tableControls}
                    className={classes.table}
                />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name={DOMAIN_NAME}
                    onRowClick={(args) => {
                        onRowClick('deliveryViaRoadInfo', args)
                    }}
                    customUrl={urls.deliveryViaRoadInfo}
                    tableConfig={deliveryViaRoadInfoConfig}
                    uniqField={UNIQ_FIELD}
                    isShowCounterTab
                    paginationModeMatches
                    showSearch
                    data-testid="deliveryViaRoadInfo"
                    tableControls={tableControls}
                    className={classes.table}
                />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name={DOMAIN_NAME}
                    onRowClick={(args) => {
                        onRowClick('deliveryViaRoadAddPickup', args)
                    }}
                    customUrl={urls.deliveryViaRoadAddPickup}
                    tableConfig={deliveryViaRoadPickUpConfig}
                    uniqField={UNIQ_FIELD}
                    isShowCounterTab
                    paginationModeMatches
                    showSearch
                    data-testid="deliveryViaRoadAddPickup"
                    tableControls={tableControls}
                    className={classes.table}
                />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name={DOMAIN_NAME}
                    onRowClick={(args) => {
                        onRowClick('deliveryViaRoadConfirmation', args)
                    }}
                    customUrl={urls.deliveryViaRoadConfirmation}
                    tableConfig={deliveryViaRoadConfirmConfig}
                    uniqField={UNIQ_FIELD}
                    isShowCounterTab
                    paginationModeMatches
                    showSearch
                    data-testid="deliveryViaRoadConfirmation"
                    tableControls={tableControls}
                    className={classes.table}
                />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name={DOMAIN_NAME}
                    onRowClick={(args) => {
                        onRowClick('pickupViaRoadConfirmation', args)
                    }}
                    customUrl={urls.pickupViaRoadConfirmation}
                    tableConfig={pickupViaRoadConfig}
                    uniqField={UNIQ_FIELD}
                    isShowCounterTab
                    paginationModeMatches
                    showSearch
                    data-testid="pickupViaRoadConfirmation"
                    tableControls={tableControls}
                    className={classes.table}
                />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name={DOMAIN_NAME}
                    onRowClick={(args) => {
                        onRowClick('airTransportByCustomerInfo', args)
                    }}
                    customUrl={urls.airTransportByCustomerInfo}
                    tableConfig={airTransportConfig}
                    uniqField={UNIQ_FIELD}
                    isShowCounterTab
                    paginationModeMatches
                    showSearch
                    data-testid="airTransportByCustomerInfo"
                    tableControls={tableControls}
                    className={classes.table}
                />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name={DOMAIN_NAME}
                    onRowClick={(args) => {
                        onRowClick('confirmationOfDropOffInfo', args)
                    }}
                    customUrl={urls.confirmationOfDropOffInfo}
                    tableConfig={getConfirmationDropOffConfig('Confirmation Of Drop-off: Info')}
                    uniqField={UNIQ_FIELD}
                    isShowCounterTab
                    paginationModeMatches
                    showSearch
                    data-testid="confirmationOfDropOffInfo"
                    tableControls={tableControls}
                    className={classes.table}
                />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name={DOMAIN_NAME}
                    onRowClick={(args) => {
                        onRowClick('confirmationOfUnloadingInfo', args)
                    }}
                    customUrl={urls.confirmationOfUnloadingInfo}
                    tableConfig={getConfirmationDropOffConfig('Confirmation Of Unloading: Info')}
                    uniqField={UNIQ_FIELD}
                    isShowCounterTab
                    paginationModeMatches
                    showSearch
                    data-testid="confirmationOfUnloadingInfo"
                    tableControls={tableControls}
                    className={classes.table}
                />
            </div>
            <div className={classes.headerTitle}>
                Internal Transports
                <div className={classes.actionWrapper}>
                    <Button
                        label="Create Internal Order"
                        onClick={openInternalCreateForm}
                        data-testid="openInternalCreateFormBtn"
                        icon={<AddIcon />}
                        className={classes.button}
                    />
                </div>
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name={DOMAIN_NAME}
                    onRowClick={(args) => {
                        onRowClick('missingInformation', args)
                    }}
                    customUrl={urls.missingInformation}
                    tableConfig={missingInformationConfig}
                    uniqField={UNIQ_FIELD}
                    isShowCounterTab
                    paginationModeMatches
                    showSearch
                    data-testid="missingInformation"
                    tableControls={tableControls}
                    className={classes.table}
                />
            </div>
            <div className={classes.headerTitle}>Container Selection</div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name={DOMAIN_NAME}
                    onRowClick={(args) => {
                        onRowClick('containerSelection', args)
                    }}
                    customUrl={urls.containerSelection}
                    tableConfig={containerSelectionConfig}
                    uniqField={UNIQ_FIELD}
                    isShowCounterTab
                    paginationModeMatches
                    showSearch
                    data-testid="containerSelection"
                    tableControls={tableControls}
                    className={classes.table}
                />
            </div>
        </div>
    )
}

export default DailyOps
